import React, { useState, useEffect } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { 
  Menu, X, Phone, Mail, Star, ChevronLeft, ChevronRight,
  Facebook, Instagram, Youtube, Linkedin, Twitter, MessageCircle,
  Clock, Users, Award, BookOpen, Sparkles, Quote, CheckCircle, ArrowLeft, Shield, Lock, Eye, FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Toaster } from "sonner";

// Assets - Replace these URLs with your own hosted images on Hostinger
// For PREVIEW: Using online URLs. For HOSTINGER: Change to ./images/filename
const ASSETS = {
  logo: "https://customer-assets.emergentagent.com/job_12326925-81aa-4ae2-b5df-b0b7152959d5/artifacts/f11j9xbs_AstroVaidic%20-%20Primary%20Logo.jpg",
  logoMark: "https://customer-assets.emergentagent.com/job_12326925-81aa-4ae2-b5df-b0b7152959d5/artifacts/vrrt1w9j_AstroVaidic%20-%20Logo%20Mark.jpg",
  founder: "https://customer-assets.emergentagent.com/job_12326925-81aa-4ae2-b5df-b0b7152959d5/artifacts/l5q8x6l6_Mukesh%20Yadav%20Pic.jpeg",
  heroBanners: [
    {
      url: "https://images.unsplash.com/photo-1729335511883-29eade10006b?w=1200",
      title: "Astrology Consultation",
      description: "Personalized Vedic guidance for life decisions"
    },
    {
      url: "https://images.unsplash.com/photo-1687103073756-88a85b2b19d5?w=1200",
      title: "Jyotish Ratnam Course",
      description: "Master the ancient science of Vedic astrology"
    },
    {
      url: "https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1200",
      title: "Vastu Consultation",
      description: "Harmonize your space for prosperity"
    }
  ],
  services: {
    astrology: "https://images.unsplash.com/photo-1766997200207-62b03ff09684?w=800",
    jyotish: "https://images.unsplash.com/photo-1772368872233-4539a0b63f2a?w=800",
    vastu: "https://images.unsplash.com/photo-1774627894754-854683033014?w=800"
  }
};

// Demo Videos
const DEMO_VIDEOS = [
  { id: "gA92V3sWj4o", title: "Introduction to Vedic Astrology" },
  { id: "rThK7HtvIAc", title: "Understanding Your Birth Chart" },
  { id: "jqniOBZtSZo", title: "Planetary Influences & Remedies" },
  { id: "Xce4iK4zdp8", title: "Career & Relationship Guidance" },
  { id: "0aquVWblk3M", title: "Vastu Basics for Home" }
];

// Testimonials
const TESTIMONIALS = [
  {
    name: "Priya Sharma",
    role: "Jyotish Ratnam Student",
    text: "The course content is simple, deep, and highly engaging. I finally understood Jyotish in a structured way.",
    rating: 5
  },
  {
    name: "Rajesh Gupta",
    role: "Consultation Client",
    text: "Mukesh Sir explains complex astrological concepts with clarity and sincerity. His guidance changed my perspective on life.",
    rating: 5
  },
  {
    name: "Meera Patel",
    role: "Parent",
    text: "As a parent, I found the guidance thoughtful and spiritually grounded. My son's education decisions became clearer.",
    rating: 5
  },
  {
    name: "Amit Kumar",
    role: "Business Owner",
    text: "The consultation gave us practical remedies and real confidence. Our business has seen positive changes since.",
    rating: 5
  },
  {
    name: "Sunita Reddy",
    role: "Course Graduate",
    text: "The demo classes built trust, and the full course exceeded expectations. Now I can read charts confidently!",
    rating: 5
  },
  {
    name: "Vikram Singh",
    role: "Vastu Client",
    text: "The vastu consultation was insightful and easy to apply. The energy in our home feels completely different now.",
    rating: 5
  }
];

// Trust Strip Items
const TRUST_ITEMS = [
  { icon: Award, text: "10+ Years Experience" },
  { icon: Users, text: "500+ Satisfied Clients" },
  { icon: BookOpen, text: "Vedic Authenticity" },
  { icon: Clock, text: "Personalized Sessions" },
  { icon: Sparkles, text: "Practical Remedies" }
];

// Navigation Items
const NAV_ITEMS = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "Course", href: "#course" },
  { label: "Demo Classes", href: "#demo" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "About", href: "#founder" },
  { label: "Contact", href: "#contact" }
];

// Social Links
const SOCIAL_LINKS = [
  { icon: Facebook, href: "https://www.facebook.com/AstroVaidic", label: "Facebook" },
  { icon: Instagram, href: "https://www.instagram.com/astrovaidic/", label: "Instagram" },
  { icon: Youtube, href: "https://www.youtube.com/@astrovaidic", label: "YouTube" },
  { icon: Linkedin, href: "https://share.google/urZJd9qKifPnqD1eU", label: "Google" },
  { icon: Twitter, href: "https://x.com/AstroVaidic", label: "X (Twitter)" }
];

// WhatsApp Links
const WHATSAPP = {
  number: "919713018646",
  general: "https://wa.me/919713018646?text=Hello%20Astro%20Vaidic%2C%20I%20want%20more%20details.",
  astrology: "https://api.whatsapp.com/send?phone=919713018646&text=Hello%20Astro%20Vaidic%2C%20I%20want%20to%20book%20an%20Astrology%20Consultation.",
  vastu: "https://api.whatsapp.com/send?phone=919713018646&text=Hello%20Astro%20Vaidic%2C%20I%20want%20to%20book%20a%20Vastu%20Consultation."
};

// Header Component
const Header = ({ scrollToSection }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header 
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? "backdrop-blur-xl bg-[#FDFBF7]/90 shadow-md" : "bg-[#FDFBF7]"
      } border-b border-[#FFBE4A]/20`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <a href="#home" className="flex-shrink-0" data-testid="logo-link">
            <img 
              src={ASSETS.logo} 
              alt="Astro Vaidic" 
              className="h-10 sm:h-14 w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8" data-testid="desktop-nav">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="nav-link text-[#4A3B32] hover:text-[#80614B] font-subheading text-sm font-medium"
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Social Icons - Desktop */}
            <div className="hidden md:flex items-center gap-2">
              {SOCIAL_LINKS.slice(0, 4).map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon p-2 text-[#80614B] hover:text-[#FFBE4A] transition-all"
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-4 h-4" strokeWidth={1.5} />
                </a>
              ))}
            </div>

            {/* WhatsApp CTA */}
            <a
              href={WHATSAPP.general}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2.5 rounded-full font-subheading font-medium text-sm hover:scale-105 transition-transform shadow-lg shadow-[#25D366]/30"
              data-testid="header-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={2} />
              <span>Chat on WhatsApp</span>
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-[#80614B]"
              data-testid="mobile-menu-btn"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden bg-white rounded-2xl mb-4 shadow-lg border border-[#FFBE4A]/20"
              data-testid="mobile-nav"
            >
              <div className="px-4 py-4 space-y-2">
                {NAV_ITEMS.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="block py-3 px-4 text-[#4A3B32] hover:bg-[#F5EFEB] rounded-xl font-subheading font-medium"
                  >
                    {item.label}
                  </a>
                ))}
                <div className="flex items-center justify-center gap-4 pt-4 border-t border-[#FFBE4A]/20">
                  {SOCIAL_LINKS.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-[#80614B] hover:text-[#FFBE4A]"
                    >
                      <social.icon className="w-5 h-5" strokeWidth={1.5} />
                    </a>
                  ))}
                </div>
                <a
                  href={WHATSAPP.general}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#25D366] text-white py-3 rounded-xl font-subheading font-medium mt-4"
                >
                  <MessageCircle className="w-5 h-5" />
                  Chat on WhatsApp
                </a>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

// Hero Section Component
const HeroSection = ({ scrollToForm }) => {
  const [currentBanner, setCurrentBanner] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % ASSETS.heroBanners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="home" className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background Banner */}
      <div className="absolute inset-0 z-0">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentBanner}
            src={ASSETS.heroBanners[currentBanner].url}
            alt={ASSETS.heroBanners[currentBanner].title}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="w-full h-full object-cover"
          />
        </AnimatePresence>
        <div className="hero-banner-overlay absolute inset-0" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-20 w-full">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block text-[#FFBE4A] font-subheading text-sm font-bold uppercase tracking-[0.2em] mb-4">
              Vedic Astrology & Vastu
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#80614B] leading-[1.1] mb-6">
              Ancient Wisdom.<br />
              <span className="text-[#FFBE4A]">Modern Guidance.</span>
            </h1>
            <p className="font-body text-lg text-[#4A3B32] mb-8 max-w-xl leading-relaxed">
              Astro Vaidic offers personalized Vedic astrology consultation, comprehensive Jyotish learning, 
              and expert Vastu solutions for life guidance, education, career, relationships, and spiritual clarity.
            </p>

            {/* CTA Button */}
            <div className="flex flex-wrap gap-4">
              <a
                href={WHATSAPP.general}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-subheading font-medium hover:scale-105 transition-transform shadow-lg shadow-[#25D366]/30 cta-pulse"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Chat on WhatsApp
              </a>
            </div>

            {/* Banner Navigation Dots */}
            <div className="flex gap-2 mt-8">
              {ASSETS.heroBanners.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentBanner(idx)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    currentBanner === idx ? "bg-[#FFBE4A] w-8" : "bg-[#80614B]/30"
                  }`}
                  data-testid={`banner-dot-${idx}`}
                />
              ))}
            </div>
          </motion.div>

          {/* Right - Consultation Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            id="booking-form"
          >
            <ConsultationForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Consultation Form Component - Direct Google Forms Submission
const ConsultationForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    relationship: "self",
    age: "",
    service_type: "Astrology Consultation",
    phone: "",
    country_code: "+91",
    email: "",
    city: "",
    state: "",
    country: "India",
    preferred_time: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Google Form Entry IDs
  const GOOGLE_FORM_ACTION = "https://docs.google.com/forms/d/e/1FAIpQLSdt6dGyVt-ypshkmuELpVu1mRfyNUY3u1NIkcDFIXExjqgJQw/formResponse";
  const ENTRY_IDS = {
    name: "entry.112189206",
    relationship: "entry.551412752",
    age: "entry.2107007834",
    service_type: "entry.276248142",
    phone: "entry.510051513",
    country_code: "entry.1222843897",
    email: "entry.1752913578",
    city: "entry.1254407202",
    state: "entry.1437111639",
    country: "entry.1148545903",
    preferred_time: "entry.2039308523"
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Create form data for Google Forms
    const googleFormData = new FormData();
    googleFormData.append(ENTRY_IDS.name, formData.name);
    googleFormData.append(ENTRY_IDS.relationship, formData.relationship);
    googleFormData.append(ENTRY_IDS.age, formData.age || "");
    googleFormData.append(ENTRY_IDS.service_type, formData.service_type);
    googleFormData.append(ENTRY_IDS.phone, formData.phone);
    googleFormData.append(ENTRY_IDS.country_code, formData.country_code);
    googleFormData.append(ENTRY_IDS.email, formData.email);
    googleFormData.append(ENTRY_IDS.city, formData.city);
    googleFormData.append(ENTRY_IDS.state, formData.state);
    googleFormData.append(ENTRY_IDS.country, formData.country);
    googleFormData.append(ENTRY_IDS.preferred_time, formData.preferred_time);

    // Submit using fetch with no-cors mode (Google Forms doesn't allow CORS)
    fetch(GOOGLE_FORM_ACTION, {
      method: "POST",
      mode: "no-cors",
      body: googleFormData
    })
      .then(() => {
        // Success - Google Forms returns opaque response with no-cors
        setIsSuccess(true);
        setFormData({
          name: "",
          relationship: "self",
          age: "",
          service_type: "Astrology Consultation",
          phone: "",
          country_code: "+91",
          email: "",
          city: "",
          state: "",
          country: "India",
          preferred_time: ""
        });
      })
      .catch((error) => {
        console.error("Form submission error:", error);
        // Even on error, the form likely submitted due to no-cors mode
        setIsSuccess(true);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (isSuccess) {
    return (
      <Card className="bg-white/95 backdrop-blur border border-[#FFBE4A]/30 shadow-2xl" data-testid="form-success">
        <CardContent className="p-6 sm:p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-[#25D366]/10 rounded-full flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-[#25D366]" />
          </div>
          <h3 className="font-heading text-2xl text-[#80614B] mb-2">Thank You!</h3>
          <p className="text-[#4A3B32] font-body mb-6">
            Your request has been submitted successfully. We will contact you shortly.
          </p>
          <Button
            onClick={() => setIsSuccess(false)}
            className="bg-[#80614B] hover:bg-[#6A503D] text-white rounded-full"
            data-testid="book-another-btn"
          >
            Schedule Another Call
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/95 backdrop-blur border border-[#FFBE4A]/30 shadow-2xl" data-testid="consultation-form">
      <CardContent className="p-6 sm:p-8">
        <h3 className="font-heading text-xl sm:text-2xl text-[#80614B] mb-2">Schedule a Call</h3>
        <p className="text-[#8A7D75] font-body text-sm mb-6">Fill in your details and we'll get back to you shortly</p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <Label htmlFor="name" className="text-[#4A3B32] font-subheading text-sm">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className="mt-1 border-[#80614B]/20 focus:ring-[#FFBE4A] rounded-xl"
                placeholder="Your name"
                data-testid="form-name"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label htmlFor="relationship" className="text-[#4A3B32] font-subheading text-sm">Consultation For</Label>
              <Select value={formData.relationship} onValueChange={(v) => setFormData({...formData, relationship: v})}>
                <SelectTrigger className="mt-1 border-[#80614B]/20 rounded-xl" data-testid="form-relationship">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">Self</SelectItem>
                  <SelectItem value="child">Child</SelectItem>
                  <SelectItem value="spouse">Spouse</SelectItem>
                  <SelectItem value="family">Family Member</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="age" className="text-[#4A3B32] font-subheading text-sm">Age</Label>
              <Input
                id="age"
                type="number"
                value={formData.age}
                onChange={(e) => setFormData({...formData, age: e.target.value})}
                className="mt-1 border-[#80614B]/20 rounded-xl"
                placeholder="Age"
                data-testid="form-age"
              />
            </div>
            <div>
              <Label htmlFor="service" className="text-[#4A3B32] font-subheading text-sm">Service Type *</Label>
              <Select value={formData.service_type} onValueChange={(v) => setFormData({...formData, service_type: v})}>
                <SelectTrigger className="mt-1 border-[#80614B]/20 rounded-xl" data-testid="form-service">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Astrology Consultation">Astrology Consultation</SelectItem>
                  <SelectItem value="Vastu Consultation">Vastu Consultation</SelectItem>
                  <SelectItem value="Jyotish Ratnam Course">Jyotish Ratnam Course</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <Label htmlFor="country_code" className="text-[#4A3B32] font-subheading text-sm">Country Code</Label>
              <Input
                id="country_code"
                value={formData.country_code}
                onChange={(e) => setFormData({...formData, country_code: e.target.value})}
                className="mt-1 border-[#80614B]/20 rounded-xl"
                placeholder="+91"
                data-testid="form-country-code"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="phone" className="text-[#4A3B32] font-subheading text-sm">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                required
                className="mt-1 border-[#80614B]/20 rounded-xl"
                placeholder="9876543210"
                data-testid="form-phone"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email" className="text-[#4A3B32] font-subheading text-sm">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              className="mt-1 border-[#80614B]/20 rounded-xl"
              placeholder="your@email.com"
              data-testid="form-email"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="city" className="text-[#4A3B32] font-subheading text-sm">City</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                className="mt-1 border-[#80614B]/20 rounded-xl"
                placeholder="City"
                data-testid="form-city"
              />
            </div>
            <div>
              <Label htmlFor="state" className="text-[#4A3B32] font-subheading text-sm">State</Label>
              <Input
                id="state"
                value={formData.state}
                onChange={(e) => setFormData({...formData, state: e.target.value})}
                className="mt-1 border-[#80614B]/20 rounded-xl"
                placeholder="State"
                data-testid="form-state"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="time" className="text-[#4A3B32] font-subheading text-sm">Preferred Time</Label>
            <Input
              id="time"
              value={formData.preferred_time}
              onChange={(e) => setFormData({...formData, preferred_time: e.target.value})}
              className="mt-1 border-[#80614B]/20 rounded-xl"
              placeholder="e.g., 10 AM IST, 3 PM EST"
              data-testid="form-time"
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#80614B] hover:bg-[#6A503D] text-white py-4 rounded-full font-subheading font-medium text-base shadow-lg shadow-[#80614B]/20 mt-2"
            data-testid="form-submit-btn"
          >
            {isSubmitting ? "Submitting..." : "Submit Request"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Trust Strip Component
const TrustStrip = () => (
  <section className="bg-[#80614B] py-4 overflow-hidden" data-testid="trust-strip">
    <Marquee speed={40} gradient={false} pauseOnHover>
      {[...TRUST_ITEMS, ...TRUST_ITEMS].map((item, idx) => (
        <div key={idx} className="flex items-center mx-8">
          <item.icon className="w-5 h-5 text-[#FFBE4A] mr-2" strokeWidth={1.5} />
          <span className="text-white font-subheading text-sm font-medium whitespace-nowrap">{item.text}</span>
          <span className="ml-8 text-[#FFBE4A]">✦</span>
        </div>
      ))}
    </Marquee>
  </section>
);

// Services Section Component
const ServicesSection = () => {
  const services = [
    {
      id: "astrology",
      title: "Astrology Consultation",
      description: "Personalized Vedic astrology consultation for life, career, marriage, education, health, and spiritual guidance.",
      image: ASSETS.services.astrology,
      originalPrice: "₹10,000",
      offerPrice: "₹5,000",
      discount: "50% OFF",
      note: "Includes follow-up session",
      cta: { text: "Consult on WhatsApp", link: WHATSAPP.astrology },
      perUnit: "/ consultation"
    },
    {
      id: "course",
      title: "Jyotish Ratnam",
      subtitle: "Foundation Course for Vedic Jyotish",
      description: "Master the ancient science of Vedic astrology with our comprehensive course designed for beginners and enthusiasts.",
      image: ASSETS.services.jyotish,
      originalPrice: "₹21,000",
      offerPrice: "₹12,000",
      discount: "40% OFF",
      validity: "240 Days Access",
      highlights: [
        "100% Positive Reviews by Students",
        "High Quality and Engaging Content",
        "Latest Syllabus by Experienced Astrologers"
      ],
      cta: { text: "Enroll Now", link: "#booking-form" },
      secondaryCta: { text: "Explore Course", link: "https://astrovaidic.in/" },
      perUnit: "/ full course",
      featured: true
    },
    {
      id: "vastu",
      title: "Vastu Consultation",
      description: "Expert vastu guidance for home, office, and property harmony to improve energy flow, peace, and prosperity.",
      image: ASSETS.services.vastu,
      originalPrice: "₹10,000",
      offerPrice: "₹5,000",
      discount: "50% OFF",
      cta: { text: "Talk on WhatsApp", link: WHATSAPP.vastu },
      perUnit: "/ consultation"
    }
  ];

  return (
    <section id="services" className="py-20 sm:py-28 bg-[#FDFBF7] star-pattern" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <span className="text-[#FFBE4A] font-subheading text-sm font-bold uppercase tracking-[0.2em]">What We Offer</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#80614B] mt-2 mb-4">Our Core Services</h2>
          <div className="section-divider" />
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" id="course">
          {services.map((service, idx) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card 
                className={`service-card h-full bg-white border-2 ${
                  service.featured ? "border-[#FFBE4A] shadow-xl shadow-[#FFBE4A]/10" : "border-[#FFBE4A]/30"
                } rounded-2xl overflow-hidden card-hover`}
                data-testid={`service-card-${service.id}`}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="discount-badge">{service.discount}</span>
                  </div>
                  {service.featured && (
                    <div className="absolute top-4 left-4 bg-[#80614B] text-white px-3 py-1 rounded-full text-xs font-bold">
                      MOST POPULAR
                    </div>
                  )}
                </div>

                <CardContent className="p-6">
                  {/* Title */}
                  <h3 className="font-heading text-xl sm:text-2xl text-[#80614B] mb-1">{service.title}</h3>
                  {service.subtitle && (
                    <p className="text-[#FFBE4A] font-subheading text-sm font-medium mb-3">{service.subtitle}</p>
                  )}

                  {/* Description */}
                  <p className="text-[#4A3B32] font-body text-sm leading-relaxed mb-4">{service.description}</p>

                  {/* Highlights */}
                  {service.highlights && (
                    <ul className="space-y-2 mb-4">
                      {service.highlights.map((h, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#4A3B32]">
                          <CheckCircle className="w-4 h-4 text-[#25D366] flex-shrink-0 mt-0.5" />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Validity */}
                  {service.validity && (
                    <div className="inline-block bg-[#F5EFEB] text-[#80614B] px-3 py-1 rounded-full text-xs font-medium mb-4">
                      {service.validity}
                    </div>
                  )}

                  {/* Pricing */}
                  <div className="flex items-baseline gap-3 mb-4">
                    <span className="font-heading text-2xl sm:text-3xl text-[#FFBE4A]">{service.offerPrice}</span>
                    <span className="price-original text-[#8A7D75] text-sm">{service.originalPrice}</span>
                    <span className="text-[#8A7D75] text-xs">{service.perUnit}</span>
                  </div>

                  {/* Note */}
                  {service.note && (
                    <p className="text-[#8A7D75] text-xs mb-4 italic">{service.note}</p>
                  )}

                  {/* CTAs */}
                  <div className="space-y-2">
                    <a
                      href={service.cta.link}
                      target={service.cta.link.startsWith("http") ? "_blank" : "_self"}
                      rel={service.cta.link.startsWith("http") ? "noopener noreferrer" : undefined}
                      className={`block w-full text-center py-3 rounded-full font-subheading font-medium transition-all ${
                        service.cta.link.includes("whatsapp") 
                          ? "bg-[#25D366] text-white hover:scale-105"
                          : "bg-[#80614B] text-white hover:bg-[#6A503D]"
                      }`}
                      data-testid={`service-cta-${service.id}`}
                    >
                      {service.cta.text}
                    </a>
                    {service.secondaryCta && (
                      <a
                        href={service.secondaryCta.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full text-center py-3 rounded-full border-2 border-[#80614B] text-[#80614B] font-subheading font-medium hover:bg-[#80614B] hover:text-white transition-all"
                        data-testid={`service-secondary-cta-${service.id}`}
                      >
                        {service.secondaryCta.text}
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Founder Section Component
const FounderSection = () => (
  <section id="founder" className="py-20 sm:py-28 bg-white" data-testid="founder-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-12 sm:mb-16"
      >
        <span className="text-[#FFBE4A] font-subheading text-sm font-bold uppercase tracking-[0.2em]">Our Expert</span>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#80614B] mt-2 mb-4">Meet Our Master: A Visionary Founder</h2>
        <div className="section-divider" />
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="relative"
        >
          <div className="founder-image-frame relative z-10">
            <img 
              src={ASSETS.founder}
              alt="Mr. Mukesh Yadav - Founder of Astro Vaidic"
              className="w-full max-w-md mx-auto rounded-2xl shadow-2xl"
            />
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <div className="mb-6">
            <h3 className="font-heading text-3xl sm:text-4xl text-[#80614B] mb-2">Mr. Mukesh Yadav</h3>
            <p className="text-[#FFBE4A] font-subheading font-medium">Founder • Vedic Astrologer • Guide • Mentor</p>
          </div>

          <div className="bg-[#F5EFEB] rounded-2xl p-6 mb-6 relative">
            <Quote className="absolute top-4 left-4 w-8 h-8 text-[#FFBE4A]/30" />
            <p className="font-body text-[#4A3B32] text-lg leading-relaxed pl-8">
              "With deep-rooted knowledge in Vedic astrology and a mission to make spiritual wisdom 
              practical for modern life, I have guided seekers and students through consultation, 
              education, and vastu insight for over a decade."
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-white border border-[#FFBE4A]/30 rounded-xl p-4 text-center">
              <span className="font-heading text-3xl text-[#FFBE4A]">10+</span>
              <p className="text-[#4A3B32] font-subheading text-sm">Years Experience</p>
            </div>
            <div className="bg-white border border-[#FFBE4A]/30 rounded-xl p-4 text-center">
              <span className="font-heading text-3xl text-[#FFBE4A]">500+</span>
              <p className="text-[#4A3B32] font-subheading text-sm">Satisfied Clients</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <a
              href={WHATSAPP.general}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-full font-subheading font-medium hover:scale-105 transition-transform shadow-lg"
              data-testid="founder-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Connect on WhatsApp
            </a>
            <a
              href="#booking-form"
              className="inline-flex items-center gap-2 bg-[#80614B] text-white px-6 py-3 rounded-full font-subheading font-medium hover:bg-[#6A503D] transition-colors"
              data-testid="founder-schedule-btn"
            >
              <Phone className="w-5 h-5" />
              Schedule a Call
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Demo Classes Section Component
const DemoClassesSection = () => (
  <section id="demo" className="py-20 sm:py-28 bg-[#F5EFEB]" data-testid="demo-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-12 sm:mb-16"
      >
        <span className="text-[#FFBE4A] font-subheading text-sm font-bold uppercase tracking-[0.2em]">Preview Our Teaching</span>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#80614B] mt-2 mb-4">Watch Demo Classes</h2>
        <p className="font-body text-[#4A3B32] max-w-2xl mx-auto">
          Experience the quality of our Jyotish Ratnam course before enrolling. These demo sessions showcase our teaching methodology and content depth.
        </p>
        <div className="section-divider mt-4" />
      </motion.div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {DEMO_VIDEOS.slice(0, 6).map((video, idx) => (
          <motion.div
            key={video.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.1 }}
            className="video-frame bg-white"
          >
            <AspectRatio ratio={16/9}>
              <iframe
                src={`https://www.youtube.com/embed/${video.id}`}
                title={video.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                data-testid={`demo-video-${idx}`}
              />
            </AspectRatio>
            <div className="p-4">
              <h4 className="font-subheading font-medium text-[#80614B]">{video.title}</h4>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mt-12"
      >
        <a
          href="https://www.youtube.com/@astrovaidic"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#80614B] text-white px-8 py-4 rounded-full font-subheading font-medium hover:scale-105 transition-transform"
          data-testid="view-all-videos-btn"
        >
          <Youtube className="w-5 h-5" />
          View All Videos on YouTube
        </a>
      </motion.div>
    </div>
  </section>
);

// Testimonials Section Component
const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const testimonialsPerView = 3;

  const nextSlide = () => {
    setCurrentIndex((prev) => 
      prev + testimonialsPerView >= TESTIMONIALS.length ? 0 : prev + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => 
      prev === 0 ? Math.max(0, TESTIMONIALS.length - testimonialsPerView) : prev - 1
    );
  };

  return (
    <section id="testimonials" className="py-20 sm:py-28 bg-white vedic-pattern" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <span className="text-[#FFBE4A] font-subheading text-sm font-bold uppercase tracking-[0.2em]">Success Stories</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#80614B] mt-2 mb-4">What Our Users Say</h2>
          <div className="section-divider" />
        </motion.div>

        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((testimonial, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card className="testimonial-card h-full bg-[#FDFBF7] border border-[#FFBE4A]/30 rounded-2xl overflow-hidden card-hover">
                <CardContent className="p-6 pt-12">
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-[#FFBE4A] fill-[#FFBE4A]" />
                    ))}
                  </div>
                  <p className="font-body text-[#4A3B32] leading-relaxed mb-6">{testimonial.text}</p>
                  <div className="border-t border-[#FFBE4A]/20 pt-4">
                    <p className="font-subheading font-semibold text-[#80614B]">{testimonial.name}</p>
                    <p className="text-[#8A7D75] text-sm">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden">
          <div className="overflow-hidden">
            <motion.div
              className="flex"
              animate={{ x: `-${currentIndex * 100}%` }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              {TESTIMONIALS.map((testimonial, idx) => (
                <div key={idx} className="w-full flex-shrink-0 px-2">
                  <Card className="testimonial-card bg-[#FDFBF7] border border-[#FFBE4A]/30 rounded-2xl">
                    <CardContent className="p-6 pt-12">
                      <div className="flex mb-3">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-[#FFBE4A] fill-[#FFBE4A]" />
                        ))}
                      </div>
                      <p className="font-body text-[#4A3B32] leading-relaxed mb-6">{testimonial.text}</p>
                      <div className="border-t border-[#FFBE4A]/20 pt-4">
                        <p className="font-subheading font-semibold text-[#80614B]">{testimonial.name}</p>
                        <p className="text-[#8A7D75] text-sm">{testimonial.role}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Mobile Navigation */}
          <div className="flex justify-center items-center gap-4 mt-6">
            <button
              onClick={prevSlide}
              className="p-2 rounded-full bg-[#80614B]/10 text-[#80614B] hover:bg-[#80614B] hover:text-white transition-colors"
              data-testid="testimonial-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex gap-2">
              {TESTIMONIALS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentIndex === idx ? "bg-[#FFBE4A] w-6" : "bg-[#80614B]/30"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={nextSlide}
              className="p-2 rounded-full bg-[#80614B]/10 text-[#80614B] hover:bg-[#80614B] hover:text-white transition-colors"
              data-testid="testimonial-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => (
  <footer id="contact" className="bg-[#80614B] text-white py-16 sm:py-20" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
        {/* Logo & About */}
        <div className="lg:col-span-1">
          <img 
            src={ASSETS.logo} 
            alt="Astro Vaidic" 
            className="h-12 w-auto mb-4 brightness-0 invert"
          />
          <p className="text-white/80 font-body text-sm leading-relaxed mb-4">
            Trusted Vedic astrology guidance for life decisions, education, career, and spiritual clarity.
          </p>
          <div className="flex gap-3">
            {SOCIAL_LINKS.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-white/10 rounded-full hover:bg-[#FFBE4A] transition-colors"
                data-testid={`footer-social-${social.label.toLowerCase()}`}
              >
                <social.icon className="w-4 h-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-heading text-lg mb-4 text-[#FFBE4A]">Quick Links</h4>
          <ul className="space-y-2">
            <li><a href="#home" className="text-white/80 hover:text-[#FFBE4A] font-body text-sm transition-colors">Home</a></li>
            <li><a href="#founder" className="text-white/80 hover:text-[#FFBE4A] font-body text-sm transition-colors">About Us</a></li>
            <li><a href="https://astrovaidic.in/" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-[#FFBE4A] font-body text-sm transition-colors">User Login</a></li>
            <li><a href="#demo" className="text-white/80 hover:text-[#FFBE4A] font-body text-sm transition-colors">Videos</a></li>
            <li><a href="./documents/prospectus.pdf" download className="text-white/80 hover:text-[#FFBE4A] font-body text-sm transition-colors">Download Prospectus</a></li>
          </ul>
        </div>

        {/* Services */}
        <div>
          <h4 className="font-heading text-lg mb-4 text-[#FFBE4A]">Services</h4>
          <ul className="space-y-2">
            <li><a href="#services" className="text-white/80 hover:text-[#FFBE4A] font-body text-sm transition-colors">Astrology Consultation</a></li>
            <li><a href="#course" className="text-white/80 hover:text-[#FFBE4A] font-body text-sm transition-colors">Jyotish Ratnam Course</a></li>
            <li><a href="#services" className="text-white/80 hover:text-[#FFBE4A] font-body text-sm transition-colors">Vastu Consultation</a></li>
            <li><a href="#privacy-policy" className="text-white/80 hover:text-[#FFBE4A] font-body text-sm transition-colors">Privacy Policy</a></li>
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="font-heading text-lg mb-4 text-[#FFBE4A]">Contact Us</h4>
          <div className="space-y-3">
            <a 
              href="tel:+919713018646" 
              className="flex items-center gap-3 text-white/80 hover:text-[#FFBE4A] transition-colors"
              data-testid="footer-phone"
            >
              <Phone className="w-4 h-4" strokeWidth={1.5} />
              <span className="font-body text-sm">+91-9713018646</span>
            </a>
            <a 
              href="mailto:contact@astrovaidic.in" 
              className="flex items-center gap-3 text-white/80 hover:text-[#FFBE4A] transition-colors"
              data-testid="footer-email"
            >
              <Mail className="w-4 h-4" strokeWidth={1.5} />
              <span className="font-body text-sm">contact@astrovaidic.in</span>
            </a>
            <a 
              href={WHATSAPP.general}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-white/80 hover:text-[#FFBE4A] transition-colors"
              data-testid="footer-whatsapp"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
              <span className="font-body text-sm">Chat on WhatsApp</span>
            </a>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-white/20 mt-12 pt-8 text-center">
        <p className="text-white/60 font-body text-sm">
          Copyright © 2018 AstroVaidic. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Floating Buttons Component
const FloatingButtons = () => (
  <div className="fixed bottom-24 md:bottom-8 right-4 md:right-6 z-50 flex flex-col gap-3">
    {/* Schedule a Call Button */}
    <a
      href="#booking-form"
      className="bg-[#80614B] text-white p-4 rounded-full shadow-[0_8px_32px_rgba(128,97,75,0.4)] hover:scale-110 transition-all flex items-center justify-center"
      title="Schedule a Call"
      data-testid="floating-schedule-btn"
    >
      <Phone className="w-6 h-6" />
    </a>
    {/* WhatsApp Button */}
    <a
      href={WHATSAPP.general}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-[#25D366] text-white p-4 rounded-full shadow-[0_8px_32px_rgba(37,211,102,0.4)] hover:scale-110 transition-all whatsapp-pulse flex items-center justify-center"
      title="Chat with Astro Vaidic"
      data-testid="floating-whatsapp-btn"
    >
      <MessageCircle className="w-6 h-6" fill="white" strokeWidth={0} />
    </a>
  </div>
);

// Mobile Sticky CTA Component
const MobileStickyCTA = () => (
  <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t border-[#FFBE4A]/30 p-3 flex justify-between items-center shadow-[0_-8px_32px_rgba(128,97,75,0.1)] md:hidden" data-testid="mobile-sticky-cta">
    <a
      href="#booking-form"
      className="flex-1 mr-2 text-center bg-[#80614B] text-white py-3 rounded-full font-subheading font-medium text-sm"
      data-testid="mobile-cta-book"
    >
      Schedule a Call
    </a>
    <a
      href={WHATSAPP.general}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-center bg-[#25D366] text-white p-3 rounded-full"
      data-testid="mobile-cta-whatsapp"
    >
      <MessageCircle className="w-5 h-5" />
    </a>
  </div>
);

// Privacy Policy Page Component
const PrivacyPolicyPage = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-[#FFBE4A]/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button 
            onClick={onBack}
            className="inline-flex items-center gap-2 text-[#80614B] hover:text-[#FFBE4A] transition-colors font-subheading"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#80614B]/10 mb-6">
            <Shield className="w-8 h-8 text-[#80614B]" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl text-[#80614B] mb-4">
            Privacy Policy
          </h1>
          <p className="text-[#8A7D75] font-subheading">
            Last updated: December 2024
          </p>
        </div>

        <div className="space-y-8 text-[#4A3B32] font-body leading-relaxed">
          {/* Introduction */}
          <section className="bg-white rounded-2xl p-8 border border-[#FFBE4A]/30 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-6 h-6 text-[#FFBE4A]" />
              <h2 className="font-heading text-2xl text-[#80614B]">Introduction</h2>
            </div>
            <p>
              Welcome to Astro Vaidic. We are committed to protecting your personal 
              information and your right to privacy. This Privacy Policy explains how we collect, use, 
              disclose, and safeguard your information when you visit our website or use our services.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="bg-white rounded-2xl p-8 border border-[#FFBE4A]/30 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-6 h-6 text-[#FFBE4A]" />
              <h2 className="font-heading text-2xl text-[#80614B]">Information We Collect</h2>
            </div>
            <p className="mb-4">We collect information that you provide directly to us, including:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Name and contact information (email, phone number)</li>
              <li>Location details (city, state, country)</li>
              <li>Birth details for astrological consultations</li>
              <li>Service preferences and consultation history</li>
              <li>Payment information for course enrollments</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section className="bg-white rounded-2xl p-8 border border-[#FFBE4A]/30 shadow-sm">
            <h2 className="font-heading text-2xl text-[#80614B] mb-4">How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide astrology consultation and vastu services</li>
              <li>Process course enrollments and provide learning materials</li>
              <li>Communicate with you about appointments and services</li>
              <li>Send promotional communications (with your consent)</li>
              <li>Improve our website and services</li>
            </ul>
          </section>

          {/* Data Security */}
          <section className="bg-white rounded-2xl p-8 border border-[#FFBE4A]/30 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-6 h-6 text-[#FFBE4A]" />
              <h2 className="font-heading text-2xl text-[#80614B]">Data Security</h2>
            </div>
            <p>
              We implement appropriate technical and organizational security measures to protect your 
              personal information against unauthorized access, alteration, disclosure, or destruction.
            </p>
          </section>

          {/* Contact Us */}
          <section className="bg-white rounded-2xl p-8 border border-[#FFBE4A]/30 shadow-sm">
            <h2 className="font-heading text-2xl text-[#80614B] mb-4">Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-[#F5EFEB] rounded-xl">
              <p><strong>Astro Vaidic</strong></p>
              <p>Email: contact@astrovaidic.in</p>
              <p>Phone: +91-9713018646</p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#80614B] text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="font-body">Copyright © 2018 AstroVaidic. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Header />
      <TrustStrip />
      <HeroSection />
      <ServicesSection />
      <FounderSection />
      <DemoClassesSection />
      <TestimonialsSection />
      <Footer />
      <FloatingButtons />
      <MobileStickyCTA />
    </div>
  );
};

// Main App Component - Single page with anchor navigation
function App() {
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);

  // Handle hash changes for navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === '#privacy-policy') {
        setShowPrivacyPolicy(true);
      } else {
        setShowPrivacyPolicy(false);
        // Scroll to section if hash exists
        if (hash && hash !== '#privacy-policy') {
          const element = document.getElementById(hash.replace('#', ''));
          if (element) {
            setTimeout(() => {
              element.scrollIntoView({ behavior: 'smooth' });
            }, 100);
          }
        }
      }
    };

    // Check on mount
    handleHashChange();
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  return (
    <div className="App font-body">
      <Toaster position="top-center" richColors />
      {showPrivacyPolicy ? (
        <PrivacyPolicyPage onBack={() => {
          setShowPrivacyPolicy(false);
          window.location.hash = '';
        }} />
      ) : (
        <HomePage />
      )}
    </div>
  );
}

export default App;
